import React from 'react';
import { Check, Palette } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { DARK_THEMES, LIGHT_THEMES } from '../themes';

const ThemeSwatch = ({ theme, active, onSelect, compact }) => (
  <button
    type="button"
    onClick={() => onSelect(theme.id)}
    className={`group relative text-left rounded-xl border transition-all ${
      compact ? 'p-2' : 'p-3'
    } ${
      active
        ? 'border-primary-500 ring-2 ring-primary-500/40 bg-primary-50/80 dark:bg-primary-950/30'
        : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 bg-white/60 dark:bg-neutral-900/40'
    }`}
    title={theme.description}
  >
    <div
      className={`rounded-lg overflow-hidden border border-black/5 dark:border-white/5 ${
        compact ? 'h-10' : 'h-14'
      }`}
      style={{ background: theme.preview.bg }}
    >
      <div className="h-full flex">
        <div className="w-1/3" style={{ background: theme.preview.surface }} />
        <div className="flex-1 flex items-end p-1.5 gap-1">
          <span
            className="inline-block rounded-full"
            style={{
              width: compact ? 10 : 14,
              height: compact ? 10 : 14,
              background: theme.preview.accent,
            }}
          />
          <span
            className="inline-block rounded-sm flex-1"
            style={{
              height: compact ? 6 : 8,
              background: theme.preview.border,
              opacity: 0.85,
            }}
          />
        </div>
      </div>
    </div>
    <div className={`flex items-start justify-between gap-2 ${compact ? 'mt-1.5' : 'mt-2.5'}`}>
      <div className="min-w-0">
        <p className={`font-medium text-neutral-900 dark:text-neutral-100 truncate ${compact ? 'text-xs' : 'text-sm'}`}>
          {theme.name}
        </p>
        {!compact && (
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
            {theme.description}
          </p>
        )}
      </div>
      {active && (
        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-600 text-white flex items-center justify-center">
          <Check className="w-3 h-3" />
        </span>
      )}
    </div>
  </button>
);

const ThemeGroup = ({ title, themes, themeId, setTheme, compact }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
      {title}
    </p>
    <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
      {themes.map((theme) => (
        <ThemeSwatch
          key={theme.id}
          theme={theme}
          active={themeId === theme.id}
          onSelect={setTheme}
          compact={compact}
        />
      ))}
    </div>
  </div>
);

const ThemePicker = ({ compact = false, showHeader = true }) => {
  const { themeId, setTheme, theme } = useTheme();

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {showHeader && (
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          <div>
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Design
            </p>
            {!compact && (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Aktiv: {theme.name}
              </p>
            )}
          </div>
        </div>
      )}
      <ThemeGroup
        title="Dunkel"
        themes={DARK_THEMES}
        themeId={themeId}
        setTheme={setTheme}
        compact={compact}
      />
      <ThemeGroup
        title="Hell"
        themes={LIGHT_THEMES}
        themeId={themeId}
        setTheme={setTheme}
        compact={compact}
      />
    </div>
  );
};

export default ThemePicker;
